import type React from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import { BarChart } from "@mui/x-charts/BarChart";
import { useLocation } from "react-router-dom";

const PIE_DATA = [
  { id: 0, value: 50.2, label: "Zimbabwe", color: "#D4A574" },
  { id: 1, value: 15.2, label: "United Kingdom", color: "#614438" },
  { id: 2, value: 14.7, label: "South Africa", color: "#8B4513" },
  { id: 3, value: 12.1, label: "Other", color: "#CD853F" },
  { id: 4, value: 7.8, label: "Australia", color: "#A0522D" },
];

const VIEWS_DATA = [
  { category: "In Church", value: 180 },
  { category: "Online", value: 120 },
];

const COMMENTS_DATA = [
  { category: "Muzukuru Website", value: 25 },
  { category: "Tribute Table", value: 8 },
];

const StreamReport: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const { state } = useLocation();
  const deceasedName = state?.deceasedName || "N/A";

  // Calculate total for percentage calculations
  const totalViews = VIEWS_DATA.reduce((sum, item) => sum + item.value, 0);
  const totalComments = COMMENTS_DATA.reduce(
    (sum, item) => sum + item.value,
    0
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: isMobile ? 2 : 4,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: isMobile ? "100%" : isTablet ? "90%" : "1200px",
          p: isMobile ? 2 : 4,
          m: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 3,
            flexDirection: isMobile ? "column" : "row",
            textAlign: isMobile ? "center" : "left",
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: 80,
              height: 80,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mr: isMobile ? 0 : 2,
              mb: isMobile ? 2 : 0,
              backgroundColor: "white",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                fontSize: "14px",
                textAlign: "center",
                lineHeight: 1.2,
                color: "#333",
              }}
            >
              MUZU
              <br />
              KURU
            </Typography>
          </Paper>
          <Typography
            variant="h4"
            sx={{
              fontWeight: "bold",
              color: "#614438",
              fontSize: isMobile ? "24px" : "28px",
              lineHeight: 1.1,
              textTransform: "uppercase",
            }}
          >
            LIVESTREAM PERFORMANCE REPORT
          </Typography>
        </Box>

        {/* Subtitle */}
        <Typography
          variant="body1"
          sx={{
            color: "#8B4513",
            mb: 3,
            fontSize: "20px",
            textAlign: "center",
            fontFamily: "sans-serif",
            fontWeight: "ExtraBold",
          }}
        >
          This report gives an insight on how the livestream was viewed
          <br />
          for <strong>{deceasedName}</strong> Funeral Service.
        </Typography>

        {/* Pie Chart Section with Percentages */}
        <Box
          sx={{
            width: "100%",
            height: isMobile ? 300 : 400,
            mb: 4,
            position: "relative",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <PieChart
            series={[
              {
                data: PIE_DATA,
                innerRadius: isMobile ? 60 : 80,
                outerRadius: isMobile ? 120 : 150,
                paddingAngle: 2,
                arcLabel: (item) => `${item.value}%`,
                arcLabelMinAngle: 10,
              },
            ]}
            width={isMobile ? 350 : 600}
            height={isMobile ? 300 : 400}
            slotProps={
              {
                //   legend: {
                //     direction: isMobile ? 'row' : 'column',
                //     position: { vertical: 'bottom', horizontal: 'middle' },
                //     padding: 0,
                //     labelStyle: {
                //       fontSize: isMobile ? 12 : 14,
                //     },
                //   },
              }
            }
            margin={{ top: 20, bottom: isMobile ? 80 : 60 }}
          />
        </Box>

        {/* Description Text */}
        <Typography
          variant="body2"
          sx={{
            color: "#8B4513",
            mb: 4,
            fontSize: "20px",
            textAlign: "center",
            maxWidth: "600px",
            lineHeight: 1.5,
            fontFamily: "sans-serif",
            fontWeight: "ExtraBold",
            margin: "0 auto",
            padding: "0 16px",
            wordBreak: "break-word",
            hyphens: "auto",
            textJustify: "inter-word",
          }}
        >
          People in different locations were able to watch the stream and join
          you in celebrating
          <br />
          <strong>{deceasedName}</strong> life.
        </Typography>

        {/* Divider */}
        <Box
          sx={{
            borderTop: "2px dotted #614438",
            width: "100%",
            maxWidth: "800px",
            my: 4,
          }}
        />

        {/* Bar Charts Section with Percentages */}
        <Grid container spacing={4} justifyContent="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography
              variant="h6"
              sx={{
                textAlign: "center",
                mb: 2,
                color: "#8B4513",
                fontSize: "16px",
                fontWeight: "bold",
              }}
            >
              VIEWS (Total: {totalViews})
            </Typography>
            <Box
              sx={{
                height: isMobile ? 250 : 300,
                width: "100%",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <BarChart
                dataset={VIEWS_DATA}
                yAxis={[{ scaleType: "band", dataKey: "category" }]}
                series={[
                  {
                    dataKey: "value",
                    color: "#8B4513",
                    label: "Views",
                    valueFormatter: (value) =>
                      value != null
                        ? `${value} (${Math.round(
                            (value / totalViews) * 100
                          )}%)`
                        : "0 (0%)",
                  },
                ]}
                layout="horizontal"
                height={isMobile ? 250 : 300}
                width={isMobile ? 350 : 500}
                margin={{
                  left: isMobile ? 80 : 100,
                  right: 20,
                  top: 20,
                  bottom: 20,
                }}
              />
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Typography
              variant="h6"
              sx={{
                textAlign: "center",
                mb: 2,
                color: "#8B4513",
                fontSize: "16px",
                fontWeight: "bold",
              }}
            >
              COMMENTS RECEIVED (Total: {totalComments})
            </Typography>
            <Box
              sx={{
                height: isMobile ? 250 : 300,
                width: "100%",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <BarChart
                dataset={COMMENTS_DATA}
                yAxis={[{ scaleType: "band", dataKey: "category" }]}
                series={[
                  {
                    dataKey: "value",
                    color: "#D4A574",
                    label: "Comments",
                    valueFormatter: (value) =>
                      value != null
                        ? `${value} (${Math.round(
                            (value / totalComments) * 100
                          )}%)`
                        : "0 (0%)",
                  },
                ]}
                layout="horizontal"
                height={isMobile ? 250 : 300}
                width={isMobile ? 350 : 500}
                margin={{
                  left: isMobile ? 100 : 120,
                  right: 20,
                  top: 20,
                  bottom: 20,
                }}
              />
            </Box>
          </Grid>
        </Grid>

        {/* Divider */}
        <Box
          sx={{
            borderTop: "2px dotted #614438",
            width: "100%",
            maxWidth: "800px",
            my: 4,
          }}
        />

        {/* Action Button */}
        <Box sx={{ textAlign: "center", width: "100%" }}>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#8B4513",
              color: "white",
              borderRadius: "20px",
              px: 4,
              py: 1.5,
              textTransform: "uppercase",
              fontSize: "14px",
              fontWeight: "bold",
              "&:hover": {
                backgroundColor: "#A0522D",
              },
            }}
          >
            CLICK HERE
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default StreamReport;
